import React, { useEffect, useState } from 'react';
import { Table, Col, Row } from 'react-bootstrap';
import getColorCode from 'src/utils/GetThemeColor';
import { useDispatch, useSelector } from 'react-redux';
import 'src/style/tableList.css';
import usePageTitle from 'src/utils/SetPageTitle';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { getPackType } from 'src/redux/Actions/VehicleTracking';
import {
  getPaginatedProducts,
  createPackTypeAssignment,
  getProductPackTypeAssignments,
} from 'src/redux/Actions/Settings';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { useLocation } from 'react-router-dom';

export default function PackTypeAssignment() {
  usePageTitle('Cloud VT • Pack Type Assignment');
  const dispatch = useDispatch();
  const location = useLocation();
  const [pageNumber, setPageNumber] = useState(1);
  const [showFunnel, setShowFunnel] = useState(false);
  const settingsReducer = useSelector((state) => state.SettingsReducer);
  const { totalPages } = settingsReducer.getProductPackTypeAssignments;
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const tenantId = authReducer?.userData?.tenant?.id;
  const vehicleTrackingReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const [selectedProducts, setSelectedProducts] = useState(null);

  const [productSearchTerm, setProductSearchTerm] = useState('');

  useEffect(() => {
    dispatch(getProductPackTypeAssignments(tenantOrganisationId, pageNumber));
    dispatch(getPackType(tenantOrganisationId));
    dispatch(getPaginatedProducts(tenantId, productSearchTerm));
  }, [dispatch, productSearchTerm]);

  const { packTypeDataList } = vehicleTrackingReducer;
  const { productList } = settingsReducer;

  const productPackTypeAssignmentsData =
    settingsReducer.getProductPackTypeAssignments.assignedPackTypes;

  const handleSelectChange = (event) => {
    setSelectedProducts(event.target.value);
    if (event.target.name === 'productId') {
      setFormData((prev) => ({
        ...prev,
        productId: event.target.value,
      }));
    }
  };

  const [formData, setFormData] = useState({
    productId: null,
    selectedPackTypes: [],
  });

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
      dispatch(getProductPackTypeAssignments(tenantOrganisationId, value));
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      const nextPage = pageNumber + 1;
      setPageNumber(nextPage);
      dispatch(getProductPackTypeAssignments(tenantOrganisationId, nextPage));
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      const prevPage = pageNumber - 1;
      setPageNumber(prevPage);
      dispatch(getProductPackTypeAssignments(tenantOrganisationId, prevPage));
    }
  };

  const packTypeOptions = packTypeDataList.map((packObj) => ({
    label: packObj.name,
    value: packObj.id,
  }));

  const productListOptions = productList.map((productObj) => ({
    label: productObj.name,
    value: productObj.id,
  }));

  const handlePackTypeAssignment = () => {
    const { productId, packTypeId } = formData;
    const packTypeAssignmentPayload = {
      tenant_organisation_id: tenantOrganisationId,
      product_id: productId,
      pack_type_id: packTypeId,
    };

    dispatch(createPackTypeAssignment(packTypeAssignmentPayload)).then(() => {
      dispatch(getPackType(tenantOrganisationId));
      dispatch(getPaginatedProducts(tenantId, productSearchTerm));
      dispatch(getProductPackTypeAssignments(tenantOrganisationId));
    });
  };

  const handleFunnelToggle = () => {
    setShowFunnel(!showFunnel);
  };

  const handleSearchInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  function handleResetFilter() {
    setFormData((prev) => ({
      ...prev,
      selectedProducts: [],
      selectedPackTypes: [],
    }));
    dispatch(getProductPackTypeAssignments(tenantOrganisationId, pageNumber));
  }

  function handleSearchFilter() {
    const searchPayload = {
      product_id:
        formData.selectedProducts.length > 0
          ? formData.selectedProducts.map((item) => item.value)
          : null,
      pack_type_id:
        formData.selectedPackTypes.length > 0
          ? formData.selectedPackTypes.map((item) => item.value)
          : null,
    };

    const params = new URLSearchParams();
    Object.entries(searchPayload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((val) => params.append(key, val));
      } else if (value) {
        params.append(key, value);
      }
    });

    const queryString = params.toString();

    dispatch(
      getProductPackTypeAssignments(
        tenantOrganisationId,
        pageNumber,
        queryString,
      ),
    );
    setPageNumber(1);
  }

  useSetBreadcrumbData(location.pathname, 'Pack Type Assignment');

  return (
    <div className="page-layout div-flex-column">
      <div className="div-space-between left-padding padding-bottom-10">
        <div className="div-flex-row cg-5">
          <Breadcrumb />
        </div>
      </div>
      <div className="div-space-between border-bottom slider-padding">
        <span className="text-xxl text-bold left-padding">
          Pack Type Assignment
        </span>
        <div className="div-flex-row div-align-center cg-20 right-padding">
          <div className="div-flex-row cg-20 div-align-center">
            <div className="list-container div-flex-column rg-5">
              <CustomToolTip title="Filter" placement="bottom">
                <button
                  aria-label="Funnel"
                  className="icon-btn-none"
                  onClick={handleFunnelToggle}
                >
                  <Funnel
                    className="icon-size-lg"
                    color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                  />
                </button>
              </CustomToolTip>
            </div>
          </div>
        </div>
      </div>
      <Row>
        <Col
          xxl={showFunnel ? 10 : 12}
          xl={showFunnel ? 9 : 12}
          lg={showFunnel ? 8 : 12}
          md={showFunnel ? 8 : 12}
          className="border-right"
        >
          <div className="setting-main-container div-flex-column rg-10 padding-top-8">
            <Row>
              <Col xxl={4} xl={4} lg={4}>
                <div className="text-sm text-semi-bold">Products</div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="productId"
                    isMulti={false}
                    className="multi-select-input-section"
                    onChange={(selectedOption) => {
                      setProductSearchTerm('');
                      handleSelectChange({
                        target: {
                          name: 'productId',
                          value: selectedOption?.value || null,
                        },
                      });
                    }}
                    onInputChange={(inputValue, { action }) => {
                      if (action === 'input-change') {
                        setProductSearchTerm(inputValue);
                      }
                    }}
                    inputValue={productSearchTerm}
                    options={productListOptions}
                    classNamePrefix="react-select"
                    placeholder="Search Products"
                    value={productListOptions.find(
                      (option) => option.value === selectedProducts,
                    )}
                    filterOption={() => true}
                  />
                  <span className="form-input-section-icon">
                    <Search className="icon-size-sm" color="#9F9F9F" />
                  </span>
                </div>
              </Col>
              <Col xxl={4} xl={4} lg={4} md={4} sm={12}>
                <div className="text-sm text-semi-bold">Pack Type</div>
                <div className="input-wrapper">
                  <CustomMultiSelect
                    name="packTypeId"
                    isMulti={false}
                    className="multi-select-input-section"
                    onChange={(selectedOption) => {
                      setFormData((prev) => ({
                        ...prev,
                        packTypeId: selectedOption
                          ? selectedOption.value
                          : null,
                      }));
                    }}
                    value={packTypeOptions.find(
                      (option) => option.value === formData.packTypeId,
                    )}
                    options={packTypeOptions}
                  />
                  <span className="form-input-section-icon">
                    <DropdownIcon
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </span>
                </div>
              </Col>
              <Col
                xxl={4}
                xl={4}
                lg={4}
                md={3}
                sm={12}
                className="flex-column div-flex-end"
              >
                <div>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handlePackTypeAssignment}
                  >
                    Add
                  </button>
                </div>
              </Col>
            </Row>
            <div className="po-table">
              <Table hover size="lg" className="table-cell-record">
                <thead>
                  <tr>
                    <th className="bg-grey">
                      <div className="text-lg text-bold table-cell-min-width-100 table-id-column-padding">
                        ID
                      </div>
                    </th>
                    <th className="bg-grey">
                      <div className="text-lg text-bold table-cell-min-width-100">
                        Product Name
                      </div>
                    </th>
                    <th className="bg-grey">
                      <div className="text-lg text-bold table-cell-min-width-100">
                        Pack Type
                      </div>
                    </th>
                    <th className="bg-grey">
                      <div className="text-lg text-bold table-cell-min-width-100">
                        Product
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="div-scrollable-details">
                  {productPackTypeAssignmentsData?.length > 0 ? (
                    productPackTypeAssignmentsData.map(
                      (productPackTypeAssignmentsDataObj) => (
                        <tr key={productPackTypeAssignmentsDataObj.id}>
                          <td className="table-cell-min-width-100">
                            <div className="div-flex-row cg-10 div-align-center">
                              <div className="text-md text-400 text-center">
                                #{productPackTypeAssignmentsDataObj.id}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell-min-width-100">
                            <div className="text-md text-400">
                              {productPackTypeAssignmentsDataObj.product_name}
                            </div>
                          </td>
                          <td className="table-cell-min-width-100">
                            <div className="text-md text-400">
                              {productPackTypeAssignmentsDataObj.pack_type}
                            </div>
                          </td>
                          <td className="table-cell-min-width-100">
                            <div className="text-md text-400">
                              {productPackTypeAssignmentsDataObj.product_code}
                            </div>
                          </td>
                        </tr>
                      ),
                    )
                  ) : (
                    <tr className="text-center">
                      <td colSpan="6">
                        <div className="text-md text-400">
                          No Pack Type Assignment
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
          <div className="footer-container left-padding padding-bottom-8">
            <div className="div-space-between div-align-center footer-content-for-page">
              <div className="div-flex-row cg-20 pagination-container div-align-center">
                <div className="div-flex-row cg-10 div-align-center">
                  <button
                    aria-label="ArrowLeft"
                    className="icon-btn-none"
                    onClick={handlePreviousPage}
                  >
                    <ArrowLeft
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                  <input
                    className="page-number-container input-icon-remove text-sm"
                    value={pageNumber < 10 ? `0${pageNumber}` : pageNumber}
                    onChange={handleInputChange}
                    type="number"
                    max={totalPages}
                  />
                  <button
                    aria-label="ArrowRight"
                    className="icon-btn-none"
                    onClick={handleNextPage}
                  >
                    <ArrowRight
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                </div>
                <span className="text-grey text-sm">
                  Page {pageNumber} of {totalPages}
                </span>
              </div>
            </div>
          </div>
        </Col>
        {showFunnel && (
          <Col
            xxl={showFunnel ? 2 : 0}
            xl={showFunnel ? 3 : 0}
            lg={showFunnel ? 4 : 0}
            md={showFunnel ? 4 : 0}
            className="p-0"
          >
            <div className="filter-list-item border-bottom">
              <span className="text-sm text-semi-bold">Filter</span>
            </div>
            <div className="div-flex-column div-space-between rg-20 right-padding">
              <div className="filter-list-item-container-scroll div-flex-column">
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Products</span>
                  <div className="div-flex-row cg-5">
                    <div className="input-wrapper">
                      <FilterMultiSelect
                        isMulti
                        name="selectedProducts"
                        className="multi-select-input-section"
                        onChange={(selectedOption) =>
                          handleSearchInputChange({
                            target: {
                              name: 'selectedProducts',
                              value: selectedOption,
                            },
                          })
                        }
                        value={formData.selectedProducts || ''}
                        options={productListOptions}
                        classNamePrefix="react-select"
                        placeholder="Search Type"
                      />
                      <span className="form-input-section-icon">
                        <ArrowRight
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                          style={{ transform: 'rotate(90deg)' }}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column">
                  <div className="div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Pack Type</span>
                    <div className="div-flex-row cg-5">
                      <div className="input-wrapper">
                        <FilterMultiSelect
                          isMulti
                          name="selectedPackTypes"
                          className="multi-select-input-section"
                          onChange={(selectedOption) =>
                            handleSearchInputChange({
                              target: {
                                name: 'selectedPackTypes',
                                value: selectedOption,
                              },
                            })
                          }
                          value={formData.selectedPackTypes || ''}
                          options={packTypeOptions}
                          classNamePrefix="react-select"
                          placeholder="Search Type"
                        />
                        <span className="form-input-section-icon">
                          <ArrowRight
                            className="icon-size-sm"
                            color={getColorCode(themeColor)}
                            style={{ transform: 'rotate(90deg)' }}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                <button
                  className={`common-btn-outlined border-${themeColor}`}
                  onClick={handleResetFilter}
                >
                  Reset
                </button>
                <button
                  className={`common-btn theme-background-${themeColor}`}
                  onClick={handleSearchFilter}
                >
                  Apply
                </button>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}
